import type { ButtonHTMLAttributes, ReactNode } from "react"
import { cn } from "@/lib/cn"

// Branded button — variants map to Valet's color tokens. Never bake a hex
// here; if a new visual surface is needed, extend the variants object so
// every page reaches it the same way.

export type ButtonVariant = "red" | "gold" | "white" | "ghost"
export type ButtonSize    = "sm" | "md" | "lg"

const VARIANT: Record<ButtonVariant, string> = {
  red:   "bg-brand-red text-white hover:bg-brand-redDeep shadow-redGlow",
  gold:  "border border-vip-gold/30 bg-vip-goldMute text-vip-goldSoft hover:bg-vip-gold/20",
  white: "bg-white text-app-bg hover:bg-app-text",
  ghost: "border border-border bg-white/5 text-app-text hover:bg-white/10",
}

// Touch-safe sizing — every size clears 36 px (sm), 44 px (md, the WCAG
// touch-target minimum), or 48 px (lg, the prototype's primary-CTA size).
// `md` is the default — most buttons reach for it. `sm` is reserved for
// tightly-packed inline rows (table action cells, badge-adjacent close
// icons). `lg` is for hero / page-header / form-submit CTAs the design
// prototype emphasizes (`min-h-12 rounded-xl px-5 py-3 text-base`).
const SIZE: Record<ButtonSize, string> = {
  sm: "min-h-9 px-3 py-1.5 text-xs rounded-control",
  md: "min-h-11 px-4 py-2 text-sm rounded-control",
  lg: "min-h-12 px-5 py-3 text-base rounded-xl font-semibold",
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  children: ReactNode
}

export function Button({ variant = "red", size = "md", className, children, ...rest }: Props) {
  return (
    <button
      {...rest}
      className={cn(
        "inline-flex items-center justify-center font-medium transition disabled:opacity-50 disabled:pointer-events-none",
        VARIANT[variant],
        SIZE[size],
        className,
      )}
    >
      {children}
    </button>
  )
}
